import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import {
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import { SimpleSelectInput } from "components/FormSelectInput";
import React from "react";
import useStyles from "styles/banner/bannerEditor";
import RewardFields, {
  describeReward,
  isRewardComplete,
  RewardOption,
  RewardSharedProps,
} from "./RewardFields";

const DAY_NAMES = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

const BASE_ACCENT = "#00897b";
const OVERRIDE_ACCENT = "#ef6c00";

type Props = RewardSharedProps & {
  slot: any;
  operatorOptions: RewardOption[];
  expanded: boolean;
  onToggle: (slotNo: number) => void;
  onSlotChange: (slotNo: number, patch: Record<string, any>) => void;
  onOverrideAdd: (slotNo: number) => void;
  onOverrideChange: (
    slotNo: number,
    index: number,
    patch: Record<string, any>,
  ) => void;
  onOverrideRemove: (slotNo: number, index: number) => void;
  /** A week that has already ended: still worth reading, no longer worth changing. */
  readOnly?: boolean;
};

const SlotCard = ({
  slot,
  operatorOptions,
  expanded,
  onToggle,
  onSlotChange,
  onOverrideAdd,
  onOverrideChange,
  onOverrideRemove,
  readOnly = false,
  ...rewardProps
}: Props) => {
  const styles = useStyles();
  const overrides = slot.operatorRewards ?? [];

  const baseComplete = isRewardComplete(slot);
  const overridesComplete = overrides.every(
    (o: any) => !!o.operator && isRewardComplete(o),
  );
  const complete = baseComplete && overridesComplete;

  const operatorLabel = (operatorId: string) =>
    operatorOptions.find((o) => o.id === operatorId)?.label ?? "Select operator";

  /**
   * An operator already configured on another row of this same day is dropped from the picker: two
   * rows for one operator would mean two rewards for the same player on one day, and the server
   * rejects the save outright. The row's own current selection is always kept so it still displays.
   */
  const takenOperators = new Set<string>(
    overrides.map((o: any) => o.operator).filter(Boolean),
  );

  const availableOperators = (current?: string) =>
    operatorOptions.filter(
      (option) => option.id === current || !takenOperators.has(option.id),
    );

  const allOperatorsConfigured =
    operatorOptions.length > 0 && takenOperators.size >= operatorOptions.length;

  return (
    <Paper
      variant="outlined"
      sx={{
        mb: 2,
        borderRadius: 2,
        overflow: "hidden",
        borderColor: complete ? `${BASE_ACCENT}55` : "divider",
      }}
    >
      {/* Header doubles as the collapsed summary, so seven days stay scannable at a glance. */}
      <Box
        onClick={() => onToggle(slot.slotNo)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: 2,
          py: 1.5,
          cursor: "pointer",
          bgcolor: "#fafafa",
          borderLeft: `4px solid ${complete ? BASE_ACCENT : "#bdbdbd"}`,
          "&:hover": { bgcolor: "#f5f5f5" },
        }}
      >
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: "50%",
            bgcolor: complete ? BASE_ACCENT : "#bdbdbd",
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 13,
            fontWeight: 700,
            flexShrink: 0,
          }}
        >
          {slot.slotNo}
        </Box>

        <Typography variant="subtitle2" sx={{ minWidth: 92 }}>
          {DAY_NAMES[slot.slotNo - 1]}
        </Typography>

        <Chip
          size="small"
          variant="outlined"
          label={`${describeReward(slot)}`}
          sx={{
            borderColor: `${BASE_ACCENT}66`,
            color: BASE_ACCENT,
            fontWeight: 600,
          }}
        />

        {overrides.length > 0 && (
          <Chip
            size="small"
            label={`${overrides.length} operator override${overrides.length > 1 ? "s" : ""}`}
            sx={{
              bgcolor: `${OVERRIDE_ACCENT}14`,
              color: OVERRIDE_ACCENT,
              fontWeight: 600,
            }}
          />
        )}

        <Box sx={{ flex: 1 }} />

        <Tooltip title={complete ? "Day configured" : "Reward not set"}>
          <Box sx={{ display: "flex", color: complete ? BASE_ACCENT : "#e65100" }}>
            {complete ? (
              <CheckCircleIcon fontSize="small" />
            ) : (
              <ErrorOutlineIcon fontSize="small" />
            )}
          </Box>
        </Tooltip>

        <ExpandMoreIcon
          fontSize="small"
          sx={{
            transition: "transform 0.2s",
            transform: expanded ? "rotate(180deg)" : "none",
            color: "text.secondary",
          }}
        />
      </Box>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        {/* The header keeps working when read-only so the admin can still open each day and read
            what it was; only the inputs below stop responding. */}
        <Box
          sx={{
            p: 2,
            ...(readOnly && { pointerEvents: "none", opacity: 0.75 }),
          }}
        >
          {/* ── Base reward ─────────────────────────────────────────── */}
          <Box
            sx={{
              border: "1px solid",
              borderColor: `${BASE_ACCENT}44`,
              borderLeft: `4px solid ${BASE_ACCENT}`,
              borderRadius: 2,
              px: 2,
              pt: 1.5,
              pb: 1,
              bgcolor: `${BASE_ACCENT}08`,
            }}
          >
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}
            >
              <Chip
                size="small"
                label="BASE REWARD"
                sx={{
                  bgcolor: BASE_ACCENT,
                  color: "white",
                  fontWeight: 700,
                  fontSize: 10,
                  letterSpacing: 0.5,
                  height: 20,
                }}
              />
              <Typography variant="caption" color="text.secondary">
                Given to <b>every operator</b> that has no override below.
              </Typography>
            </Box>

            <RewardFields
              value={slot}
              fieldPrefix={`slot_${slot.slotNo}`}
              onChange={(patch) => onSlotChange(slot.slotNo, patch)}
              {...rewardProps}
            />
          </Box>

          {/* ── Operator overrides ──────────────────────────────────── */}
          <Box sx={{ mt: 2.5, pl: { xs: 0, md: 3 } }}>
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}
            >
              <Typography
                variant="subtitle2"
                sx={{ color: OVERRIDE_ACCENT, fontWeight: 700 }}
              >
                Operator level rewards
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Each row <b>completely replaces</b> the base reward above — for
                that one operator, on this day only.
              </Typography>
            </Box>

            {overrides.length === 0 && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 1.5, fontStyle: "italic" }}
              >
                None — every operator gets the base reward on{" "}
                {DAY_NAMES[slot.slotNo - 1]}.
              </Typography>
            )}

            {overrides.map((override: any, index: number) => (
              <Paper
                key={index}
                variant="outlined"
                sx={{
                  px: 2,
                  pt: 1.5,
                  pb: 1,
                  mb: 1.5,
                  borderRadius: 2,
                  borderColor: `${OVERRIDE_ACCENT}55`,
                  borderLeft: `4px solid ${OVERRIDE_ACCENT}`,
                  bgcolor: `${OVERRIDE_ACCENT}0a`,
                }}
              >
                {/* The operator is the first thing this row asks for, so its picker leads and the
                    badge plus summary sit beside it. The badge group is bottom-aligned at the
                    select's own height, which lands it on the select's centre line rather than
                    floating up against the field label. */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-end",
                    flexWrap: "wrap",
                    gap: 2,
                    mb: 1.5,
                  }}
                >
                  <Box className={styles.field}>
                    <SimpleSelectInput
                      label="Operator"
                      name={`slot_${slot.slotNo}_override_${index}_operator`}
                      value={override.operator ?? ""}
                      options={availableOperators(override.operator)}
                      onChange={(e: any) =>
                        onOverrideChange(slot.slotNo, index, {
                          operator: e.target.value,
                        })
                      }
                    />
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      flex: 1,
                      minWidth: 240,
                      height: 38,
                    }}
                  >
                    <Chip
                      size="small"
                      label="OPERATOR OVERRIDE"
                      sx={{
                        bgcolor: OVERRIDE_ACCENT,
                        color: "white",
                        fontWeight: 700,
                        fontSize: 10,
                        letterSpacing: 0.5,
                        height: 20,
                      }}
                    />
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                      {operatorLabel(override.operator)} →{" "}
                      {describeReward(override)}
                    </Typography>
                    <Box sx={{ flex: 1 }} />
                    {!readOnly && (
                      <Tooltip title="Remove this override">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => onOverrideRemove(slot.slotNo, index)}
                        >
                          <RemoveCircleOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </Box>

                <RewardFields
                  value={override}
                  fieldPrefix={`slot_${slot.slotNo}_override_${index}`}
                  onChange={(patch) =>
                    onOverrideChange(slot.slotNo, index, patch)
                  }
                  {...rewardProps}
                />
              </Paper>
            ))}

            {/* With every reachable operator already overridden there is nothing left to pick, so
                the button would only ever add a row with an empty dropdown. */}
            {!readOnly &&
              (allOperatorsConfigured ? (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", fontStyle: "italic" }}
                >
                  Every operator this cycle reaches already has a reward on{" "}
                  {DAY_NAMES[slot.slotNo - 1]}.
                </Typography>
              ) : (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddCircleOutlineIcon />}
                  onClick={() => onOverrideAdd(slot.slotNo)}
                  sx={{
                    color: OVERRIDE_ACCENT,
                    borderColor: `${OVERRIDE_ACCENT}88`,
                    "&:hover": {
                      borderColor: OVERRIDE_ACCENT,
                      bgcolor: `${OVERRIDE_ACCENT}0a`,
                    },
                  }}
                >
                  Add operator level reward
                </Button>
              ))}
          </Box>
        </Box>
      </Collapse>
    </Paper>
  );
};

export default SlotCard;
